import { ref } from "vue";

const STORAGE_KEY = "mivu_build_jobs";

const POLL_MAX_ATTEMPTS = 180; // 180 * 10s = 30 min
const POLL_INTERVAL_MS = 10000;


const TERMINAL = ["completed", "failed"];

const jobs = ref(loadJobs());

function loadJobs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error("Failed to load build jobs from localStorage:", e);
    return [];
  }
}

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs.value));
  } catch (e) {
    console.error("Failed to persist build jobs:", e);
  }
}

function findJob(id) {
  return jobs.value.find((j) => j.id === id);
}

function patchJob(id, patch) {
  const job = findJob(id);
  if (!job) return;
  Object.assign(job, patch);
  persist();
}


// The builder now pushes the finished installer to LicConf itself (server-to-
// server), so polling is all that's left: mirror the builder's status into the
// local job for live labels and return the terminal job body, which carries
// productId/productType/productLink once the push has completed.
async function pollBuildStatus(jobId, serverUrl) {
  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
    let job;
    try {
      const response = await fetch(`${serverUrl}/build-status/${jobId}`);
      if (!response.ok) throw new Error("Failed to check build status");
      job = await response.json();
    } catch (error) {
      console.error("Error polling build status:", error);
      if (attempt >= POLL_MAX_ATTEMPTS - 1) throw error;
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      continue;
    }

    if (job.status === "completed") return job;
    if (job.status === "failed") throw new Error(job.error || "Build failed");

    patchJob(jobId, { status: job.status });

    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
  throw new Error("Build timeout - exceeded maximum wait time (30 minutes)");
}


async function driveJob(job) {
  try {
    patchJob(job.id, { status: "building", error: null });
    const result = await pollBuildStatus(job.id, job.serverUrl);

    patchJob(job.id, {
      status: "completed",
      productId: result.productId ?? null,
      productType:
        result.productType ||
        (job.save?.configAssociation === "Agent" ? "Agent" : "NoAgent"),
      productLink: result.productLink || "",
    });

  } catch (error) {
    console.error(`Build job ${job.id} failed:`, error);
    patchJob(job.id, {
      status: "failed",
      error: error.message || "Build failed",
    });
  }
}

export const buildJobsStore = {
  jobs,

  activeCount() {
    return jobs.value.filter((j) => !TERMINAL.includes(j.status)).length;
  },

  // Register a job once /build-installer-v2 has returned a jobId, then drive it in
  // the background. Fire-and-forget: callers must not await this.
  registerJob(meta) {
    const job = {
      id: meta.jobId,
      serverUrl: meta.serverUrl,
      os: meta.os || "",
      filename: meta.filename || "MivuInstaller",
      label: meta.label || meta.filename || "MivuInstaller",
      status: "building",
      error: null,
      productId: null,
      productLink: "",
      createdAt: meta.createdAt || null,
      save: meta.save || {},
    };
    // Replace any stale entry with the same id.
    jobs.value = jobs.value.filter((j) => j.id !== job.id);
    jobs.value.push(job);
    persist();
    driveJob(job);
    return job;
  },

  // Re-attach to any in-flight jobs after a reload / app start.
  resumeAll() {
    for (const job of jobs.value) {
      if (!TERMINAL.includes(job.status)) {
        driveJob(job);
      }
    }
  },

  removeJob(id) {
    jobs.value = jobs.value.filter((j) => j.id !== id);
    persist();
  },

  clearCompleted() {
    jobs.value = jobs.value.filter((j) => !TERMINAL.includes(j.status));
    persist();
  },
};

export default buildJobsStore;
